// packages/engine/src/process-scaffold.ts
//
// WalkResult + SwimlaneContext → ProcessScaffold. The one place semantic
// resolution touches a walk; placements (swimlane, BPMN) consume the scaffold
// and never see the context. See process-scaffold-types.ts for the contract.

import { ANONYMOUS_INTERFACE_NAME } from "./types.js";
import type { QualifiedName } from "./types.js";
import type { WalkResult, WalkStep, WalkNode, NodeId } from "./process-walker-types.js";
import { isAwaitNodeId } from "./process-walker-types.js";
import type { SwimlaneContext } from "./process-swimlane-types.js";
import type {
  ProcessScaffold,
  ScaffoldBranch,
  ScaffoldNode,
  ScaffoldStep,
} from "./process-scaffold-types.js";

/**
 * The first executable step within a node list — recursing into loops,
 * branches and nested subprocess bodies — or null when there is none. Anchors
 * a `do` node in the lane of its first callee (otherwise it would inherit
 * whatever lane the preceding step happened to land on, which is arbitrary).
 */
function firstStepIn(nodes: readonly WalkNode[]): WalkStep | null {
  for (const n of nodes) {
    if (n.kind === "step") return n;
    let kids: readonly WalkNode[] | undefined;
    if (n.kind === "each") kids = n.children;
    else if (n.kind === "do") kids = n.body;
    else if (n.kind === "parallel") kids = n.branches.flatMap((b) => b.children);
    else if (n.kind === "if") kids = [...n.branches.flatMap((b) => b.children), ...(n.elseBranch ? n.elseBranch.children : [])];
    else if (n.kind === "switch") kids = n.cases.flatMap((c) => c.children);
    else if (n.kind === "try") kids = [...n.tryBranch.children, ...n.catches.flatMap((c) => c.children)];
    if (kids && kids.length) {
      const r = firstStepIn(kids);
      if (r) return r;
    }
  }
  return null;
}

/** `each <label>[ in <source>]` display label — or `retry <bound>` for a retry
 *  loop (PROCESS.md §4), so it reads distinctly from a plain iteration. */
function eachLabel(n: Extract<WalkNode, { kind: "each" }>): string {
  const lbl = n.label ? n.label : "";
  if (n.retry) return `retry${lbl ? " " + lbl : ""}`;
  const src = n.source ? ` in ${n.source}` : "";
  return `each${lbl ? " " + lbl : ""}${src}`;
}

/** A wait step (`await <head>`) — its walker nodeId's leaf is `await:…`. */
function isAwaitScaffoldStep(n: ScaffoldNode): n is ScaffoldStep {
  return n.kind === "step" && isAwaitNodeId(n.nodeId);
}

/**
 * PROCESS.md §6: a MULTI-await head (consecutive leading awaits) is any-of
 * multiple-start. The walk-only desugar demotes it to a leading `parallel race`
 * over the awaits (so the linear trace keeps OR, not AND). For RENDERING that
 * race is a fiction — there is no gateway before the process has started — so
 * unwrap it back to the stacked trigger awaits. Fires only when the leading node
 * is a `parallel` whose every branch is a single TRIGGER await — the exact shape
 * the walker's trigger derivation flags as multiple-start (§2.1), regardless of
 * how the merge was written. So an authored leading `parallel race { await X }
 * { await Y }` of pure awaits IS treated as any-of start (§6 — it is one), while
 * a genuine wait-or-timeout race (a branch with `await` + `fail`, or an inbound
 * step) has non-single-await branches and stays a gateway. Both render backends
 * consume the unwrapped scaffold — one truth.
 */
function unwrapMultiAwaitStart(
  tree: readonly ScaffoldNode[],
  triggers: ReadonlySet<NodeId>,
): readonly ScaffoldNode[] {
  const head = tree[0];
  if (!head || head.kind !== "branched" || head.sub !== "parallel") return tree;
  if (head.groups.length < 2) return tree;
  const awaits: ScaffoldStep[] = [];
  for (const g of head.groups) {
    const only = g.children.length === 1 ? g.children[0]! : null;
    if (!only || !isAwaitScaffoldStep(only) || !triggers.has(only.nodeId)) return tree;
    awaits.push(only);
  }
  return [...awaits, ...tree.slice(1)];
}

export function buildScaffold(walk: WalkResult, ctx: SwimlaneContext): ProcessScaffold {
  // Control-owner resolution (each/if/select/try/parallel prefixes): same
  // actor-first-then-module walk the steps get.
  function controlOwner(raw: QualifiedName | undefined):
    | { owner: QualifiedName; ownerIsActor: boolean }
    | Record<string, never> {
    if (!raw || raw.length === 0) return {};
    const isActor = ctx.isActor(raw);
    const path = isActor ? raw : ctx.resolveCallerModule(raw) ?? raw;
    return { owner: path, ownerIsActor: isActor };
  }
  function resolveStep(ws: WalkStep): ScaffoldStep {
    const fromIsActor = ctx.isActor(ws.from);
    const fromModule = fromIsActor ? null : ctx.resolveCallerModule(ws.from);
    // Anonymous callee: `to` is the bare module path — pad it with the
    // synthetic interface segment so the module survives the trailing strip.
    const toModule = ctx.resolveCalleeModule(
      ws.toAnonymous ? ([...ws.to, ANONYMOUS_INTERFACE_NAME] as QualifiedName) : ws.to,
    );
    return {
      kind: "step",
      nodeId: ws.nodeId,
      stepIndex: ws.stepIndex,
      from: ws.from,
      to: ws.to,
      ...(ws.toAnonymous !== undefined ? { toAnonymous: ws.toAnonymous } : {}),
      ...(ws.fromInstance !== undefined ? { fromInstance: ws.fromInstance } : {}),
      ...(ws.toInstance !== undefined ? { toInstance: ws.toInstance } : {}),
      ...(ws.direction !== undefined ? { direction: ws.direction } : {}),
      fromIsActor,
      fromModule,
      toModule,
      ...(ws.bind !== undefined ? { bind: ws.bind } : {}),
      ...(ws.args !== undefined ? { args: ws.args } : {}),
      ...(ws.asName !== undefined ? { asName: ws.asName } : {}),
      ...(ws.reentryTarget !== undefined ? { reentryTarget: ws.reentryTarget } : {}),
      ...(ws.noteText !== undefined ? { noteText: ws.noteText } : {}),
      ...(ws.prose !== undefined ? { prose: ws.prose } : {}),
      ...(ws.comment !== undefined ? { comment: ws.comment } : {}),
      modifiers: ws.modifiers,
    };
  }

  const linear: ScaffoldStep[] = walk.steps.map(resolveStep);
  const byIndex = new Map<number, ScaffoldStep>(linear.map((s) => [s.stepIndex, s]));

  function convertStep(ws: WalkStep): ScaffoldStep {
    // Tree steps and walk.steps entries share stepIndex; reuse the linear
    // object so tree/linear step identities coincide.
    return byIndex.get(ws.stepIndex) ?? resolveStep(ws);
  }

  function convertBranch(b: { label: string; selected: boolean; children: readonly WalkNode[] }): ScaffoldBranch {
    return { label: b.label, selected: b.selected, children: convertNodes(b.children) };
  }

  function convertNodes(nodes: readonly WalkNode[]): ScaffoldNode[] {
    const out: ScaffoldNode[] = [];
    for (const n of nodes) {
      if (n.kind === "step") {
        out.push(convertStep(n));
      } else if (n.kind === "each") {
        out.push({
          kind: "each",
          nodeId: n.nodeId,
          ...controlOwner(n.owner),
          label: eachLabel(n),
          ...(n.source ? { iterates: true } : {}),
          ...(n.retry ? { retry: true } : {}),
          children: convertNodes(n.children),
        });
      } else if (n.kind === "if") {
        const groups = [
          ...n.branches.map(convertBranch),
          ...(n.elseBranch ? [{ ...convertBranch(n.elseBranch), isElse: true as const }] : []),
        ];
        out.push({
          kind: "branched",
          sub: "if",
          nodeId: n.nodeId,
          ...controlOwner(n.owner),
          label: `if ${groups[0]?.label ?? "if"}`,
          groups,
        });
      } else if (n.kind === "switch") {
        out.push({
          kind: "branched",
          sub: "switch",
          nodeId: n.nodeId,
          ...controlOwner(n.owner),
          label: `switch${n.head ? " " + n.head : ""}`,
          groups: n.cases.map(convertBranch),
          ...(n.runCount !== undefined ? { runCount: n.runCount } : {}),
        });
      } else if (n.kind === "try") {
        out.push({
          kind: "branched",
          sub: "try",
          nodeId: n.nodeId,
          ...controlOwner(n.owner),
          label: "try",
          groups: [convertBranch(n.tryBranch), ...n.catches.map(convertBranch)],
        });
      } else if (n.kind === "parallel") {
        out.push({
          kind: "branched",
          sub: "parallel",
          nodeId: n.nodeId,
          ...controlOwner(n.owner),
          label: "parallel",
          // Parallel branches all run — selected is forced true (legacy).
          groups: n.branches.map((b) => ({
            label: b.label,
            selected: true,
            children: convertNodes(b.children),
          })),
          ...(n.mergeAspect !== undefined ? { mergeAspect: n.mergeAspect } : {}),
        });
      } else if (n.kind === "do") {
        const argList = n.args && n.args.length ? `(${n.args.join(", ")})` : "()";
        const expanded = !!(n.expanded && n.body);
        // Anchor: first executable step of the body — the tree body when
        // expanded, the collapsed group's first linear step otherwise.
        // Resolved from the RAW `to`, unpadded and instance-free (legacy
        // quirk; see process-scaffold-types.ts).
        let firstStep = firstStepIn(n.body ?? []);
        if (!firstStep) {
          const grp = walk.collapsedGroups.get(n.nodeId);
          if (grp) firstStep = walk.steps[grp.startIndex] ?? null;
        }
        out.push({
          kind: "do",
          nodeId: n.nodeId,
          label: `${n.target}${argList}`,
          target: n.target,
          ...(n.args !== undefined ? { args: n.args } : {}),
          ...(n.comment !== undefined ? { comment: n.comment } : {}),
          expanded,
          ...(n.recursive !== undefined ? { recursive: n.recursive } : {}),
          ...(expanded ? { body: convertNodes(n.body!) } : {}),
          ...(firstStep ? { anchorModule: ctx.resolveCalleeModule(firstStep.to) } : {}),
        });
      } else if (n.kind === "terminator") {
        out.push({
          kind: "terminator",
          nodeId: n.nodeId,
          type: n.type,
          ...(n.reason !== undefined ? { reason: n.reason } : {}),
          ...(n.all !== undefined ? { all: n.all } : {}),
          ...(n.comp !== undefined ? { comp: n.comp } : {}),
          ...(n.synthesized !== undefined ? { synthesized: n.synthesized } : {}),
        });
      }
    }
    return out;
  }

  return {
    tree: unwrapMultiAwaitStart(convertNodes(walk.tree), new Set(walk.triggers)),
    linear,
    byIndex,
    collapsedGroups: walk.collapsedGroups,
    parallelGroups: walk.parallelGroups,
    reentries: walk.reentries,
    compensation: walk.compensation,
    triggers: walk.triggers,
    diagnostics: walk.diagnostics,
  };
}
